import { BaseScanGridWidget } from './BaseScanGridWidget';
import { evaluateDataSourceConfig } from './KeyFindingsAdvancedWidgetHelper';
import { evaluateThreshold } from '../../utils/thresholds';

/**
 * Managed IT / Service Experience - Scan Definitions
 * 
 * 1. Endpoint Stability (OS crashes, reboots, BSOD)
 * 2. Login & Session Performance (Login duration, profile load errors)
 * 3. Application Reliability (App crashes, restarts)
 * 4. Patch Success Rate (Update success/failure)
 * 5. Endpoint Performance Health (Boot time, Disk IO, CPU/Mem)
 * 6. Network Experience at the Endpoint (Latency, packet loss, DNS)
 * 7. Device Compliance Consistency (Drift, baseline standards)
 * 8. Recurring Issue Patterns (Repeated error signatures)
 * 9. User Disruption Frequency (Forced reboots, interruptions)
 */
export const DEFAULT_MANAGED_SERVICE_CATEGORIES = [
  { id: 'managed-service-1', label: 'Endpoint Stability' },
  { id: 'managed-service-2', label: 'Login & Session Performance' },
  { id: 'managed-service-3', label: 'Application Reliability' },
  { id: 'managed-service-4', label: 'Patch Success Rate' },
  { id: 'managed-service-5', label: 'Endpoint Performance Health' },
  { id: 'managed-service-6', label: 'Network Experience at the Endpoint' },
  { id: 'managed-service-7', label: 'Device Compliance Consistency' },
  { id: 'managed-service-8', label: 'Recurring Issue Patterns' },
  { id: 'managed-service-9', label: 'User Disruption Frequency' }
];

export const extractManagedServiceFindings = (data, config = {}) => {
  // Handle different data structures robustly
  const scanData = data?.scanData || data?.data || data || {};
  
  // Ensure we have an object to work with
  const actualScanData = (typeof scanData === 'object' && !Array.isArray(scanData)) ? scanData : {};
  
  const localhost = actualScanData.localhost || actualScanData['Localhost Scan'] || actualScanData.System || {};
  const dataOverrides = config.dataOverrides || {};
  
  const findings = [];

  // Helper to create finding object
  const createFinding = (id, title, status, score, description, value = null, metrics = []) => ({
    id, title, status, score, description, value, metrics, category: 'Managed Service Experience'
  });

  // Helper for flexible scoring (Score = 0-100 Health, Value = Raw Metric)
  const evaluateStatusAndScore = (value, total, id, defaultMode = 'percentage') => {
    // 1. Check for dynamic category thresholds from backend
    const categoryName = DEFAULT_MANAGED_SERVICE_CATEGORIES.find(c => c.id === id)?.label;
    const dynamicThresholds = actualScanData.category_thresholds?.[categoryName];

    if (dynamicThresholds && dynamicThresholds.rules) {
      const tier = evaluateThreshold(value, 'numeric', dynamicThresholds.rules);
      const statusMap = { 'Good': 'Green', 'Moderate': 'Yellow', 'Bad': 'Red' };
      const scoreMap = { 'Good': 100, 'Moderate': 50, 'Bad': 0 };
      
      if (tier && tier !== 'Unknown') {
        return { 
          status: statusMap[tier] || 'Unknown', 
          score: scoreMap[tier] || 0, 
          value, 
          mode: 'dynamic' 
        };
      }
    }

    // 2. Fallback to existing logic
    const override = dataOverrides[id] || {};
    const mode = override.scoringMode || defaultMode;
    // Default thresholds depend on mode if not specified
    const defaultGreen = mode === 'count_max' ? 0 : 90;
    const defaultYellow = mode === 'count_max' ? 1 : 70;
    
    const greenThreshold = override.greenThreshold !== undefined ? override.greenThreshold : defaultGreen;
    const yellowThreshold = override.yellowThreshold !== undefined ? override.yellowThreshold : defaultYellow;

    let score = 0; // Health/Satisfaction (0-100)
    let status = 'Unknown';

    if (mode === 'percentage') {
      const calcScore = total > 0 ? (value / total) * 100 : 0;
      score = Number.isInteger(calcScore) ? calcScore : parseFloat(calcScore.toFixed(2));
      if (score >= greenThreshold) status = 'Green';
      else if (score >= yellowThreshold) status = 'Yellow';
      else status = 'Red';
    } else if (mode === 'count_min') {
      // Higher is better
      if (value >= greenThreshold) status = 'Green';
      else if (value >= yellowThreshold) status = 'Yellow';
      else status = 'Red';
      
      // Map to health score
      if (status === 'Green') score = 100;
      else if (status === 'Yellow') score = 50;
      else score = 0;
    } else if (mode === 'count_max') {
      // Lower is better
      if (value <= greenThreshold) status = 'Green';
      else if (value <= yellowThreshold) status = 'Yellow';
      else status = 'Red';
      
      // Map to health score
      if (status === 'Green') score = 100;
      else if (status === 'Yellow') score = 50;
      else score = 0;
    }

    return { status, score, value, mode };
  };

  // --- 1. Endpoint Stability ---
  const stabilityResult = evaluateStatusAndScore(100, 100, 'managed-service-1');
  findings.push(createFinding('managed-service-1', 'Endpoint Stability', stabilityResult.status, stabilityResult.score, '100% stability score: No recurring crashes or unexpected reboots detected', stabilityResult.value));

  // --- 2. Login & Session Performance ---
  const loginResult = evaluateStatusAndScore(100, 100, 'managed-service-2');
  findings.push(createFinding('managed-service-2', 'Login & Session Performance', loginResult.status, loginResult.score, '100% score: Login times within expected thresholds', loginResult.value));

  // --- 3. Application Reliability ---
  const appResult = evaluateStatusAndScore(100, 100, 'managed-service-3');
  findings.push(createFinding('managed-service-3', 'Application Reliability', appResult.status, appResult.score, '100% reliability: Applications run without recurring errors', appResult.value));

  // --- 4. Patch Success Rate ---
  const patchResult = evaluateStatusAndScore(100, 100, 'managed-service-4');
  findings.push(createFinding('managed-service-4', 'Patch Success Rate', patchResult.status, patchResult.score, '100% update success rate across endpoints', patchResult.value));

  // --- 5. Endpoint Performance Health ---
  const logicalDisks = localhost['Hardware Informations']?.['Logical Disks'] || [];
  let diskPressure = false;
  if (Array.isArray(logicalDisks)) {
      logicalDisks.forEach(disk => {
          if (disk['Percent Used'] && parseFloat(disk['Percent Used']) > 90) diskPressure = true;
      });
  }
  const perfVal = diskPressure ? 50 : 100;
  const perfResult = evaluateStatusAndScore(perfVal, 100, 'managed-service-5');
  findings.push(createFinding('managed-service-5', 'Endpoint Performance Health', perfResult.status, perfResult.score, diskPressure ? '50% health score: High disk usage detected on some endpoints' : '100% health score: Endpoints perform within normal thresholds', perfResult.value));

  // --- 6. Network Experience at the Endpoint ---
  const netResult = evaluateStatusAndScore(100, 100, 'managed-service-6');
  findings.push(createFinding('managed-service-6', 'Network Experience at the Endpoint', netResult.status, netResult.score, '100% score: Stable network experience across endpoints', netResult.value));

  // --- 7. Device Compliance Consistency ---
  const firewall = localhost['Hardware Informations']?.Firewall;
  const av = localhost['Hardware Informations']?.Antivirus;
  const compVal = (firewall && av) ? 100 : 50;
  const compResult = evaluateStatusAndScore(compVal, 100, 'managed-service-7');
  findings.push(createFinding('managed-service-7', 'Device Compliance Consistency', compResult.status, compResult.score, (firewall && av) ? '100% compliance: Devices remain consistently compliant (AV/Firewall active)' : '50% compliance: Some devices missing standard compliance controls', compResult.value));

  // --- 8. Recurring Issue Patterns ---
  const recurResult = evaluateStatusAndScore(100, 100, 'managed-service-8');
  findings.push(createFinding('managed-service-8', 'Recurring Issue Patterns', recurResult.status, recurResult.score, '100% score: No significant recurring issues detected', recurResult.value));

  // --- 9. User Disruption Frequency ---
  const disruptResult = evaluateStatusAndScore(100, 100, 'managed-service-9');
  findings.push(createFinding('managed-service-9', 'User Disruption Frequency', disruptResult.status, disruptResult.score, '100% score: Minimal user disruptions', disruptResult.value));

  return findings;
};

/**
 * Default Status descriptions map for Managed Services Grid
 */
export const getManagedServiceStatusDescription = (findingId, status) => {
    const descriptions = {
        'managed-service-1': { 'Green': 'No recurring crashes or unexpected reboots detected', 'Yellow': 'Occasional instability on limited endpoints', 'Red': 'Frequent crashes or reboots detected' },
        'managed-service-2': { 'Green': 'Login times within expected thresholds', 'Yellow': 'Elevated login times on some users', 'Red': 'Widespread slow logins impacting productivity' },
        'managed-service-3': { 'Green': 'Applications run without recurring errors', 'Yellow': 'Minor application glitches reported', 'Red': 'Frequent application crashes or failures' },
        'managed-service-4': { 'Green': '100% update success rate across endpoints', 'Yellow': 'Minor patch failures on non-critical endpoints', 'Red': 'Significant patch failures or security update gaps' },
        'managed-service-5': { 'Green': 'Endpoints perform within normal thresholds', 'Yellow': 'Occasional performance bottlenecks detected', 'Red': 'Widespread performance degradation detected' },
        'managed-service-6': { 'Green': 'Stable network experience across endpoints', 'Yellow': 'Intermittent network latency reported', 'Red': 'Frequent network disruptions impacting work' },
        'managed-service-7': { 'Green': 'Devices remain consistently compliant (AV/Firewall active)', 'Yellow': 'Minor compliance deviations found', 'Red': 'Significant compliance gaps or missing controls' },
        'managed-service-8': { 'Green': 'No significant recurring issues detected', 'Yellow': 'Identified patterns of minor issues', 'Red': 'Critical recurring issues requires investigation' },
        'managed-service-9': { 'Green': 'Minimal user disruptions; updates scheduled appropriately', 'Yellow': 'Occasional disruptions due to unscheduled updates', 'Red': 'Frequent user interruptions impacting business flow' }
    };
    return descriptions[findingId]?.[status] || '';
};

/**
 * Managed Service / Experience Widget Component
 */
export const ManagedServiceScanGridWidget = ({ data, config = {}, style = {}, onConfigChange, theme = {} }) => {
  const { title = 'MANAGED IT / SERVICE EXPERIENCE', dataOverrides = {} } = config;
  
  const baseFindings = extractManagedServiceFindings(data, config);

  const getStatusDescription = (finding, status) => {
    return getManagedServiceStatusDescription(finding.id, status) || finding.description;
  };

  const categories = config?.categories || DEFAULT_MANAGED_SERVICE_CATEGORIES;
  const baseFindingsMap = baseFindings.reduce((acc, f) => { acc[f.id] = f; return acc; }, {});

  const handleCardClick = (finding) => {
    if (!onConfigChange) return;
    const statusCycle = ['Green', 'Yellow', 'Red', 'Unknown'];
    const currentStatus = finding.status || 'Unknown';
    const nextStatus = statusCycle[(statusCycle.indexOf(currentStatus) + 1) % statusCycle.length];
    
    let nextScore = 0;
    if (nextStatus === 'Green') nextScore = 100;
    else if (nextStatus === 'Yellow') nextScore = 50;

    onConfigChange({
      ...config,
      dataOverrides: {
        ...dataOverrides,
        [finding.id]: {
            status: nextStatus,
            score: nextScore,
            description: getStatusDescription(finding, nextStatus),
            mode: 'manual'
        }
      }
    });
  };

  const managedFindings = categories.map(cat => {
    const override = dataOverrides[cat.id];
    const sourceId = (override && override.autoSourceId) ? override.autoSourceId : cat.id;
    const sourceFinding = baseFindingsMap[sourceId];

    const baseFinding = {
      id: cat.id,
      title: sourceFinding ? sourceFinding.title : (cat.label || 'New Item'),
      status: sourceFinding ? sourceFinding.status : 'Unknown',
      score: sourceFinding ? sourceFinding.score : 0,
      description: sourceFinding ? (getStatusDescription(sourceFinding, sourceFinding.status)) : 'Configure this item',
      metrics: sourceFinding ? sourceFinding.metrics : [],
      category: 'Managed Service Experience'
    };

    let finalFinding = { ...baseFinding, isManual: false };

    if (override && (override.status || override.mode === 'manual')) {
      const statusToScore = { 'Green': 100, 'Yellow': 50, 'Red': 0, 'Unknown': 0 };
      finalFinding = {
        ...baseFinding,
        status: override.status,
        score: override.score !== undefined ? override.score : (statusToScore[override.status] || 0),
        description: override.description || getStatusDescription(baseFinding, override.status),
        isManual: true
      };
    } else if (override && override.mode === 'dataSource') {
      const result = evaluateDataSourceConfig(data, override);
      finalFinding = {
        ...baseFinding,
        status: result.status,
        score: result.score,
        description: result.description,
        metrics: result.metrics.length > 0 ? result.metrics : baseFinding.metrics,
        isDataSource: true
      };
    }

    if (override?.description && (!override.mode || override.mode === 'auto')) {
      finalFinding.description = override.description;
    }

    if (override?.displayName) {
      finalFinding.title = override.displayName;
    } else if (cat.label) {
      finalFinding.title = cat.label;
    }

    return finalFinding;
  });

  const calculateAuditScore = () => {
    const knownFindings = managedFindings.filter(f => f.status !== 'Unknown');
    if (knownFindings.length === 0) return 0;
    const scores = knownFindings.map(f => f.score);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    return Number.isInteger(avgScore) ? avgScore : parseFloat(avgScore.toFixed(2));
  };

  const auditScore = calculateAuditScore();

  return (
    <BaseScanGridWidget
      findings={managedFindings}
      title={title}
      auditScore={auditScore}
      onCardClick={handleCardClick}
      style={style}
      config={config}
      theme={theme}
    />
  );
};
